import React, {CSSProperties, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import classNames from 'classnames';

import {remToPx} from '@dvrd/dvr-controls';

interface Props {
    text: React.ReactNode;
    className?: string;
    /** Keep the tooltip open while the pointer is over it, so links and other
     * content inside stay clickable. */
    interactive?: boolean;
    children: React.ReactElement;
}

const SCREEN_PADDING = remToPx(1);
const HIDE_DELAY_MS = 150;

export default function Tooltip(props: Props) {
    const {text, children, className, interactive = false} = props;
    const [visible, setVisible] = useState(false);
    const [style, setStyle] = useState<CSSProperties>({top: 0, left: 0});
    const ref = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const hideTimeout = useRef<number | undefined>(undefined);

    function show() {
        if (!text || !ref.current) return;
        window.clearTimeout(hideTimeout.current);
        // Already shown — the pointer returned to the anchor within the
        // interactive grace window. Just cancel the pending hide; do NOT
        // recompute the raw position, or we'd drop the clamp/flip that
        // fixPosition applied and the tooltip would jump back off-screen.
        if (visible) return;
        const rect = ref.current.getBoundingClientRect();
        setStyle({
            top: rect.top - 4,
            left: rect.left + rect.width / 2
        });
        setVisible(true);
    }

    function hide() {
        if (!interactive) {
            setVisible(false);
            return;
        }
        // Grace period so the pointer can travel from the anchor onto the
        // tooltip without it disappearing underneath.
        window.clearTimeout(hideTimeout.current);
        hideTimeout.current = window.setTimeout(() => setVisible(false), HIDE_DELAY_MS);
    }

    function cancelHide() {
        window.clearTimeout(hideTimeout.current);
    }

    useEffect(() => () => window.clearTimeout(hideTimeout.current), []);

    /** Clamp tooltips that fall off a screen edge back into view: shift
     * horizontally along the top placement, flip below the anchor when there
     * is no room above. */
    function fixPosition() {
        const tooltip = tooltipRef.current;
        const anchor = ref.current;
        if (!tooltip || !anchor) return;
        const rect = tooltip.getBoundingClientRect();
        const next: CSSProperties = {...style};
        let changed = false;
        const maxRight = window.innerWidth - SCREEN_PADDING;
        if (rect.left < SCREEN_PADDING) {
            next.left = (next.left as number) + (SCREEN_PADDING - rect.left);
            changed = true;
        } else if (rect.right > maxRight) {
            next.left = (next.left as number) - (rect.right - maxRight);
            changed = true;
        }
        if (rect.top < SCREEN_PADDING) {
            next.top = anchor.getBoundingClientRect().bottom + 4;
            next.transform = 'translate(-50%, 0)';
            changed = true;
        }
        if (changed) setStyle(next);
    }

    // Clamp once per open. Deps are ONLY [visible] on purpose: keying on
    // `style` would re-run this after fixPosition's own setStyle, and because
    // fixPosition writes a fresh style object whenever it enters a clamp branch
    // it would re-trigger itself indefinitely (a tooltip wider than the viewport
    // oscillates between the two horizontal clamps) — "Maximum update depth
    // exceeded". The grace-period re-entry that used to un-clamp the position is
    // handled in show() instead (it no longer resets style while visible).
    useLayoutEffect(() => {
        if (visible) fixPosition();
    }, [visible]);

    return (
        <>
            {React.cloneElement(children, {
                // @ts-expect-error – ref forwarding for DOM elements
                ref,
                onMouseEnter: show,
                onMouseLeave: hide
            })}
            {visible &&
                text &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={classNames('sr-tooltip-portal', interactive && 'interactive', className)}
                        style={style}
                        onMouseEnter={interactive ? cancelHide : undefined}
                        onMouseLeave={interactive ? hide : undefined}
                    >
                        {text}
                    </div>,
                    document.body
                )}
        </>
    );
}
